/** The steps of a filing as a line of marks: done, in progress, blocked, not yet, not checked.
 *
 * Two densities. `full` is the filing page's spine: each step has its label and a few words
 * (counts, not a sentence). `compact` is a row of a table: the marks alone, with the same words
 * for a screen reader, so a list of twelve filings is scanned by shape and read by name.
 *
 * **State is a shape and a word, never colour alone** (design rule 4): the glyph differs per state
 * and every step carries its state in text (visible in `full`, hidden in `compact`). The line
 * between marks is decoration. A step is a statement the server made (`filingSpine.ts` says which),
 * so this component only draws - it decides nothing and counts nothing.
 */

import { CheckCircle, Circle, CircleNotch, MinusCircle, Question, XCircle } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";

import { STATE_WORD } from "../screens/filingSpine";
import type { SpineStep, StepState } from "../screens/filingSpine";
import styles from "./FilingSpine.module.css";

const GLYPH: Record<StepState, { icon: Icon; weight: "fill" | "bold" | "regular" }> = {
  done: { icon: CheckCircle, weight: "fill" },
  current: { icon: CircleNotch, weight: "bold" },
  blocked: { icon: XCircle, weight: "fill" },
  ahead: { icon: Circle, weight: "regular" },
  unknown: { icon: Question, weight: "bold" },
  na: { icon: MinusCircle, weight: "regular" },
};

export function FilingSpine({
  steps,
  label,
  density = "full",
  surface = "paper",
  testId,
}: {
  steps: readonly SpineStep[];
  /** What the list is, for a screen reader: "Where this filing stands". */
  label: string;
  density?: "full" | "compact";
  /** `hero` draws on the dark panel's tokens; `paper` on a panel. */
  surface?: "hero" | "paper";
  testId?: string;
}) {
  return (
    <ol
      className={`${styles.spine} ${density === "compact" ? styles.compact : styles.full}`}
      data-surface={surface}
      data-testid={testId}
      aria-label={label}
    >
      {steps.map((step) => {
        const { icon: Glyph, weight } = GLYPH[step.state];
        return (
          <li key={step.key} className={styles.step} data-state={step.state} data-step={step.key}>
            <span className={styles.mark} aria-hidden="true">
              <Glyph size={density === "compact" ? 14 : 18} weight={weight} />
            </span>
            {density === "full" ? (
              <span className={styles.text}>
                <span className={styles.label}>{step.label}</span>
                <span className={styles.note}>
                  <span className="visuallyHidden">{STATE_WORD[step.state]}: </span>
                  {step.note}
                </span>
              </span>
            ) : (
              <span className="visuallyHidden">
                {step.label}: {STATE_WORD[step.state]}
                {step.note ? `, ${step.note}` : ""}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
